import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Sidebar from "../components/Sidebar";
import logoImage from "../assets/images/logo.svg";

const ResponsiveSidebar = styled.div`
  @media (max-width: 900px) {
    display: none;
  }
`;

const ResponsiveMain = styled.div`
  margin-left: 240px;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;

  @media (max-width: 900px) {
    margin-left: 0;
  }
`;

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const MobileHeader = styled.div`
  display: none;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  padding-left: max(12px, env(safe-area-inset-left));
  padding-right: max(16px, env(safe-area-inset-right));
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 50;
  flex-shrink: 0;

  @media (max-width: 900px) {
    display: flex;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const BrandLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
`;

const BrandName = styled.span`
  font-size: 16px;
  font-weight: 500;
  font-style: italic;
  color: #111;
  white-space: nowrap;
`;

const HamburgerButton = styled.button`
  width: 44px;
  height: 44px;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  background: transparent;
  border: none;
  padding: 10px 8px;
  cursor: pointer;
  flex-shrink: 0;

  span {
    display: block;
    height: 2px;
    width: 100%;
    background: #222;
    border-radius: 2px;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 150;
  animation: ${fadeIn} 160ms ease-out;
  @media (min-width: 901px) {
    display: none;
  }
`;

const MobileSidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  height: 100dvh;
  width: min(240px, 86vw);
  background: #fff;
  z-index: 200;
  animation: ${slideIn} 200ms ease-out;
`;

const SidebarLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  return (
    <LayoutContainer>
      <ResponsiveSidebar>
        <Sidebar />
      </ResponsiveSidebar>
      <ResponsiveMain>
        <MobileHeader>
          <HamburgerButton
            aria-label="Open sidebar"
            onClick={() => setMobileOpen(true)}
          >
            <span></span>
            <span></span>
            <span></span>
          </HamburgerButton>
          <Brand>
            <BrandLogo src={logoImage} alt="" />
            <BrandName>Lytte.ai</BrandName>
          </Brand>
        </MobileHeader>
        <Outlet />
      </ResponsiveMain>

      {mobileOpen && (
        <MobileOverlay onClick={() => setMobileOpen(false)}>
          <MobileSidebarWrapper onClick={(e) => e.stopPropagation()}>
            <Sidebar />
          </MobileSidebarWrapper>
        </MobileOverlay>
      )}
    </LayoutContainer>
  );
};

export default SidebarLayout;
